import { useState, useEffect } from 'react';
import './EventLogs.css';
import { eventLogAPI } from '../../../config/event-log-api';
import { toast } from 'react-toastify';
import { EVENT_LOG_TYPES } from '../../../constants/eventLogs';
import { Copy, X, History, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const EventLogs = ({ incomingFilter }) => {
  const navigate = useNavigate();
  const [eventLogs, setEventLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({
    type: '',
    userId: '',
    dateFilter: "today",
    startDate: '',
    endDate: '',
    query: ''
  });
  const [firstLoad, setFirstLoad] = useState(true);
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [isExternalSearch, setIsExternalSearch] = useState(false);
  const [externalTitle, setExternalTitle] = useState('');
  const [externalEntityType, setExternalEntityType] = useState('product');
  const [returnContext, setReturnContext] = useState(null);

  const fetchEventLogs = async (page = 1, type = '', userId = '', dateFilter = 'today', startDate = '', endDate = '', query = '') => {
    try {
      setLoading(true);
      setError(null);

      const response = await eventLogAPI.getEventLogsByFilter(userId, type, page, 20, dateFilter, startDate, endDate, query);

      if (response.success) {
        setEventLogs(response.data || []);
        setPagination(response.pagination || {});
      } else {
        setError(response.message || 'Failed to fetch event logs');
        toast.error(response.message || 'Failed to fetch event logs');
      }
    } catch (error) {
      console.error('Error fetching event logs:', error);
      setError('Error fetching event logs: ' + error.message);
      toast.error('Error fetching event logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await eventLogAPI.getEventLogUsers();
      if (response.success) {
        setUsers(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    const hasExternalFilter = incomingFilter?.query || incomingFilter?.userId || incomingFilter?.type;

    if (hasExternalFilter) {
      const newFilters = {
        ...filters,
        type: incomingFilter.type || '',
        query: incomingFilter.query || '',
        userId: incomingFilter.userId || '',
        dateFilter: incomingFilter.dateFilter || 'all_time'
      };
      setFilters(newFilters);
      setIsExternalSearch(true);
      setExternalTitle(incomingFilter.productName || incomingFilter.entityName || '');
      setExternalEntityType(incomingFilter.entityType || 'product');
      setReturnContext(incomingFilter.returnTo || null);
      fetchEventLogs(1, newFilters.type, newFilters.userId, newFilters.dateFilter, newFilters.startDate, newFilters.endDate, newFilters.query);

      navigate('/dashboard', {
        replace: true,
        state: {
          page: 'event-logs',
          returnTo: incomingFilter.returnTo || null,
        },
      });
    } else {
      fetchEventLogs(1, '', '', 'today', '', '');
      fetchUsers();
    }
    setFirstLoad(false);
  }, []);

  const handleClearExternalSearch = () => {
    setIsExternalSearch(false);
    setExternalTitle('');
    setExternalEntityType('product');
    setReturnContext(null);
    const defaultFilters = {
      type: '',
      userId: '',
      dateFilter: "today",
      startDate: '',
      endDate: '',
      query: ''
    };
    setFilters(defaultFilters);
    fetchEventLogs(1, '', '', 'today', '', '');
    fetchUsers();
  };

  const getBackButtonLabel = () => {
    if (!returnContext) return 'Back';

    if (returnContext.page === 'orders') {
      const ref = returnContext.orderReference || returnContext.openOrderId;
      return ref ? `Back to order ${ref}` : 'Back to order';
    }

    if (returnContext.page === 'product-list') {
      const name = returnContext.productName || returnContext.openProductId;
      return name ? `Back to product ${name}` : 'Back to product';
    }

    return 'Back';
  };

  const handleBackToSource = () => {
    if (!returnContext?.page) return;

    navigate('/dashboard', {
      state: {
        page: returnContext.page,
        ...returnContext,
      },
    });
  };

  useEffect(() => {
    if (firstLoad) return;
    fetchEventLogs(currentPage, filters.type, filters.userId, filters.dateFilter, filters.startDate, filters.endDate, filters.query);
  }, [currentPage]);

  const handleFilterChange = (filterType, value) => {
    if (filterType === 'dateFilter') {
      if (value === "custom") {
        setShowCustomDate(true);
        setFilters(prev => ({ ...prev, dateFilter: value, startDate: '', endDate: '' }));
      } else {
        setShowCustomDate(false);
        setFilters(prev => ({ ...prev, dateFilter: value, startDate: '', endDate: '' }));
      }
    } else {
      setFilters(prev => ({ ...prev, [filterType]: value }));
    }
  };

  const applyFilters = () => {
    if (filters.dateFilter === "custom") {
      if (!filters.startDate || !filters.endDate) {
        toast.error('Please select both start and end dates.');
        return;
      }

      const start = new Date(filters.startDate);
      const end = new Date(filters.endDate);
      if (start > end) {
        toast.error('Start date cannot be after end date.');
        return;
      }
    }

    setCurrentPage(1);
    fetchEventLogs(1, filters.type, filters.userId, filters.dateFilter, filters.startDate, filters.endDate, filters.query);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
    }
  };

  const getEventTypeLabel = (type) => {
    if (!type) return '';

    return type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getEventTypeBadgeClass = (type) => {
    if (!type) return 'eventlog-type-default';
    if (type.includes('create') || type.includes('add')) {
      return 'eventlog-type-add';
    }
    if (type.includes('edit') || type.includes('update')) {
      return 'eventlog-type-edit';
    }
    if (type.includes('delete')) {
      return 'eventlog-type-delete';
    }
    return 'eventlog-type-default';
  };

  const getSearchPlaceholder = () => {
    if (!filters.type) return "Search by ProductId or OrderId";

    if (filters.type.includes('product')) {
      return "Search by ProductId";
    }

    if (filters.type.includes('order')) {
      return "Search by OrderId";
    }

    return "Search by ProductId or OrderId";
  };

  const handleLogClick = (log) => {
    setSelectedLog(log);
    setIsViewDialogOpen(true);
  };

  const handleCopy = (value, key) => {
    navigator.clipboard.writeText(value);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
  };

  const idFields = [
    { key: "productId", label: "Product ID" },
    { key: "id", label: "ID" },
    { key: "_id", label: "ID" },
    { key: "routeId", label: "Route Id" },
    { key: "collectionId", label: "Collection ID" },
    { key: "subCategoryId", label: "Sub-Category ID" },
    { key: "userId", label: "User ID" },
    { key: "requestId", label: "Request ID" }
  ];

  return (
    <div className="eventlog-page">
      <div className="eventlog-page-header">
        <div className="eventlog-page-title">
          {isExternalSearch ? (
            <div className="eventlog-external-header">
              {returnContext && (
                <button type="button" className="eventlog-back-btn" onClick={handleBackToSource}>
                  <ArrowLeft size={16} />
                  {getBackButtonLabel()}
                </button>
              )}
              <div>
                <h1>Admin logs for {externalEntityType} <span className="eventlog-id-highlight">{filters.query}</span> {externalTitle}</h1>
                <p>View and track all system events and activities</p>
              </div>
            </div>
          ) : (
            <>
              <h1>Admin Logs</h1>
              <p>View and track all system events and activities</p>
            </>
          )}
        </div>
      </div>

      {!isExternalSearch && (
        <div className="eventlog-filter-section">
          <div className="eventlog-filter-form">

            <div className="eventlog-filter-group">
              <label>Event Type</label>
              <select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className="eventlog-filter-select"
              >
                <option value="">All Types</option>
                {Object.entries(EVENT_LOG_TYPES).map(([key, value]) => (
                  <option key={value} value={value}>
                    {getEventTypeLabel(value)}
                  </option>
                ))}
              </select>
            </div>

            <div className="eventlog-filter-group">
              <label>User</label>
              <select
                value={filters.userId}
                onChange={(e) => handleFilterChange('userId', e.target.value)}
                className="eventlog-filter-select"
              >
                <option value="">All</option>
                {users.map((user) => (
                  <option key={user._id} value={user._id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="eventlog-filter-group">
              <label>Date Range</label>
              <select
                value={filters.dateFilter}
                onChange={(e) => handleFilterChange('dateFilter', e.target.value)}
                className="eventlog-filter-select"
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                {filters.dateFilter === 'all_time' && <option value="all_time">All Time</option>}
                <option value="custom">Custom Date</option>
              </select>
            </div>

            {showCustomDate && (
              <div className="eventlog-custom-date-fields">
                <div className="eventlog-filter-group eventlog-custom-date-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={filters.startDate ? filters.startDate.split('T')[0] : ''}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    className="eventlog-filter-input"
                  />
                </div>

                <div className="eventlog-filter-group eventlog-custom-date-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={filters.endDate ? filters.endDate.split('T')[0] : ''}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    className="eventlog-filter-input"
                  />
                </div>
              </div>
            )}

            <div className="eventlog-filter-group" style={{ flexGrow: 1 }}>
              <label>Search Query</label>
              <input
                type="text"
                placeholder={getSearchPlaceholder()}
                value={filters.query}
                onChange={(e) => handleFilterChange('query', e.target.value)}
                className="eventlog-filter-input"
              />
            </div>
            <button onClick={applyFilters} className="eventlog-apply-filters-button">
              Apply Filters
            </button>
          </div>
        </div>
      )}

      <div className="eventlog-table-container">
        {loading ? (
          <div className="eventlog-loading-state">
            <div className="eventlog-loading-spinner"></div>
            <p className="eventlog-loading-text">Loading event logs...</p>
          </div>
        ) : error ? (
          <div className="eventlog-error-state">
            <p>{error}</p>
            <button
              onClick={() => fetchEventLogs(currentPage, filters.type, filters.userId, filters.dateFilter, filters.startDate, filters.endDate, filters.query)}
              className="eventlog-retry-button"
            >
              Retry
            </button>
          </div>
        ) : eventLogs.length === 0 ? (
          <div className="eventlog-empty-state">
            <p>No event logs found</p>
          </div>
        ) : (
          <>
            <div className="eventlog-table-wrapper">
              <table className="eventlog-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Type</th>
                    <th>Date & Time</th>
                    <th>User</th>
                  </tr>
                </thead>
                <tbody>
                  {eventLogs.map((log) => (
                    <tr key={log._id}>
                      <td className="eventlog-log-title-cell">
                        <span
                          className="eventlog-log-title-text"
                          onClick={() => handleLogClick(log)}
                        >
                          {log.title}
                        </span>
                      </td>
                      <td>
                        <span className={`eventlog-type-badge ${getEventTypeBadgeClass(log.type)}`}>
                          {getEventTypeLabel(log.type)}
                        </span>
                      </td>
                      <td>{log.date || log.createdAt || '-'}</td>
                      <td>{log.user?.name || log.user?.data?.name || log.eventUserId || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="eventlog-pagination">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!pagination.hasPrevPage || loading}
                  className="eventlog-pagination-button"
                >
                  Previous
                </button>

                <span className="eventlog-pagination-info">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>

                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.hasNextPage || loading}
                  className="eventlog-pagination-button"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {isViewDialogOpen && selectedLog && (
        <div className="eventlog-dialog-overlay">
          <div className="eventlog-dialog-box eventlog-view-log-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="eventlog-dialog-header">
              <h2>Event Log Details</h2>
              <button className="eventlog-close-btn" onClick={() => setIsViewDialogOpen(false)}>
                &times;
              </button>
            </div>

            <div className="eventlog-log-details-content">
              <div className="eventlog-detail-box eventlog-title-box">
                <span className="eventlog-detail-box-header">Title</span>
                <div className="eventlog-detail-box-content">{selectedLog.title}</div>
              </div>

              <div className="eventlog-detail-row-container">
                <div className="eventlog-detail-box eventlog-half-width">
                  <span className="eventlog-detail-box-header">Date & Time</span>
                  <div className="eventlog-detail-box-content">
                    {selectedLog.date || selectedLog.createdAt || '-'}
                  </div>
                </div>

                <div className="eventlog-detail-box eventlog-half-width">
                  <span className="eventlog-detail-box-header">Type</span>
                  <div className="eventlog-detail-box-content">
                    {getEventTypeLabel(selectedLog.type)}
                  </div>
                </div>
              </div>

              <div className="eventlog-detail-box">
                <span className="eventlog-detail-box-header">Admin Info</span>
                <div className="eventlog-detail-box-content eventlog-user-info-content">
                  {selectedLog.user?._id || selectedLog.user?.email || selectedLog.user?.name || selectedLog.user?.data ? (
                    <>
                      <div className="eventlog-user-info-row">
                        <span className="eventlog-user-info-key">Admin ID:</span>
                        <span className="eventlog-user-info-value">{selectedLog.user._id || selectedLog.user.data._id}</span>
                      </div>
                      <div className="eventlog-user-info-row">
                        <span className="eventlog-user-info-key">Name:</span>
                        <span className="eventlog-user-info-value">{selectedLog.user.name || selectedLog.user.data.name}</span>
                      </div>
                      <div className="eventlog-user-info-row">
                        <span className="eventlog-user-info-key">Email:</span>
                        <span className="eventlog-user-info-value">{selectedLog.user.email || selectedLog.user.data.email}</span>
                      </div>
                    </>
                  ) : (
                    <div className="eventlog-user-info-row">
                      <span className="eventlog-user-info-key">Event User ID:</span>
                      <span className="eventlog-user-info-value">{selectedLog.eventUserId}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="eventlog-detail-box">
                <div className="eventlog-detail-box-content eventlog-id-list">
                  {idFields.map(({ key, label }) => {
                    const value = selectedLog.payload[key];
                    if (!value) return null;

                    return (
                      <div className="eventlog-id-item" key={key}>
                        <span className="eventlog-id-label">{label}:</span>
                        <span className="eventlog-id-text">{value}</span>
                        <button className="eventlog-copy-btn" onClick={() => handleCopy(value, key)} title={`Copy ${label}`}>
                          <div className="eventlog-copy-icon-container">
                            <Copy size={14} />
                          </div>
                        </button>

                        {copiedKey === key && <span className="eventlog-copied-text">Copied!</span>}
                      </div>
                    );
                  })}
                </div>
              </div>

              {selectedLog.description && (
                <div className="eventlog-detail-box eventlog-description-box">
                  <span className="eventlog-detail-box-header">Description</span>
                  <div className="eventlog-detail-box-content">{selectedLog.description}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventLogs;
